/* Maturity Assessment — 5-level Lean maturity across customizable pillars */
import ToolWizard from "./ToolWizard";
import { Plus, Trash2, FileText } from "lucide-react";

const DEFAULT_PILLARS = [
  { id: "leadership",     label: "Leadership Engagement", desc: "Visible sponsorship, gemba walks, coaching" },
  { id: "strategy",       label: "Strategy Deployment",   desc: "Hoshin, OGSM, X-matrix, breakthrough goals" },
  { id: "voc",            label: "Voice of Customer",     desc: "CTQs, NPS, customer-driven priorities" },
  { id: "process",        label: "Process Excellence",    desc: "VSM, standard work, A3 problem-solving" },
  { id: "people",         label: "People & Culture",      desc: "Skills matrix, kaizen culture, idea pipeline" },
  { id: "tech",           label: "Technology & Data",     desc: "Visual mgmt, automation, decision data" },
];

const LEVEL_DEFS = [
  { lvl: 1, label: "Initial",   desc: "Reactive · firefighting · undocumented" },
  { lvl: 2, label: "Aware",     desc: "Pockets of practice · pilots running" },
  { lvl: 3, label: "Defined",   desc: "Standardised · routinely measured" },
  { lvl: 4, label: "Managed",   desc: "Predictable · cross-functional · improving" },
  { lvl: 5, label: "Optimising", desc: "Self-correcting · benchmark · innovating" },
];

const LEVEL_COLOR = ["#94A3A0", "#DC2626", "#D97706", "#3B82F6", "#7C9A7E", "#16A34A"];

export default function MaturityWizard({ project, onSave, onExportPDF }) {
  const steps = [
    {
      key: "setup", label: "Setup",
      render: ({ data, updateData }) => {
        const pillars = data.pillars && data.pillars.length ? data.pillars : DEFAULT_PILLARS.map(p => ({ ...p }));
        const setPillars = (list) => updateData({ pillars: list });
        const addPillar = () => setPillars([...pillars, { id: `p_${Date.now()}`, label: "", desc: "" }]);
        const updPillar = (i, p) => setPillars(pillars.map((m, idx) => idx === i ? { ...m, ...p } : m));
        const rmPillar  = (i) => setPillars(pillars.filter((_, idx) => idx !== i));
        const reset     = () => setPillars(DEFAULT_PILLARS.map(p => ({ ...p })));
        return (
          <div className="space-y-3" data-testid="maturity-setup">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2"><label className="block text-xs font-semibold text-sage-600 mb-1">Assessment scope</label><input value={data.scope || ""} onChange={e => updateData({ scope: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" placeholder="e.g. Operations division" /></div>
              <div><label className="block text-xs font-semibold text-sage-600 mb-1">Assessor</label><input value={data.assessor || ""} onChange={e => updateData({ assessor: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-sage-700">Pillars ({pillars.length})</p>
              <button data-testid="maturity-reset-pillars" onClick={reset} className="text-xs text-sage-500 hover:text-sage-700 underline">Reset to defaults</button>
            </div>
            <div className="space-y-2">
              {pillars.map((p, i) => (
                <div key={p.id} className="bg-white border border-sage-200 rounded-md p-2 grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                  <input data-testid={`maturity-pillar-${i}-label`} placeholder="Pillar name" value={p.label} onChange={e => updPillar(i, { label: e.target.value })} className="md:col-span-4 border border-sage-200 rounded-md px-2 py-1.5 text-sm font-semibold" />
                  <input placeholder="Description / scope" value={p.desc} onChange={e => updPillar(i, { desc: e.target.value })} className="md:col-span-7 border border-sage-200 rounded-md px-2 py-1.5 text-xs text-sage-600" />
                  <button onClick={() => rmPillar(i)} className="text-red-500 justify-self-center"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <button data-testid="maturity-add-pillar" onClick={addPillar} className="flex items-center gap-1 border border-dashed border-sage-300 text-sage-600 rounded-md px-3 py-2 text-sm hover:bg-sage-50 w-full justify-center"><Plus className="w-3.5 h-3.5" /> Add custom pillar</button>
            </div>
          </div>
        );
      },
    },
    {
      key: "score", label: "Score",
      render: ({ data, updateData }) => {
        const pillars = data.pillars || DEFAULT_PILLARS;
        const scores = data.scores || {};
        const setScore = (id, p) => updateData({ scores: { ...scores, [id]: { ...(scores[id] || {}), ...p } } });
        return (
          <div className="space-y-3" data-testid="maturity-score">
            <div className="bg-sage-50 rounded-md p-2 text-xs text-sage-600">Score current state and target state per pillar (1 = Initial → 5 = Optimising). Add evidence notes to support each rating.</div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-1 text-xs">
              {LEVEL_DEFS.map((l, i) => (
                <div key={l.lvl} className="rounded-md p-2 text-white" style={{ backgroundColor: LEVEL_COLOR[i + 1] }}>
                  <p className="font-bold">{l.lvl} · {l.label}</p>
                  <p className="text-[10px]">{l.desc}</p>
                </div>
              ))}
            </div>
            {pillars.map(p => {
              const s = scores[p.id] || { current: 1, target: 3, evidence: "" };
              return (
                <div key={p.id} className="bg-white border border-sage-200 rounded-md p-3 space-y-2" data-testid={`maturity-pillar-${p.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="font-semibold text-sage-900 text-sm">{p.label}</p>
                      <p className="text-xs text-sage-500">{p.desc}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs font-semibold text-sage-600">Current ({s.current || 1})</label>
                      <input data-testid={`maturity-${p.id}-current`} type="range" min="1" max="5" step="1" value={s.current || 1} onChange={e => setScore(p.id, { current: Number(e.target.value) })} className="w-full" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-sage-600">Target ({s.target || 3})</label>
                      <input data-testid={`maturity-${p.id}-target`} type="range" min="1" max="5" step="1" value={s.target || 3} onChange={e => setScore(p.id, { target: Number(e.target.value) })} className="w-full" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-sage-600">Gap</label>
                      <p className="font-heading text-lg font-bold" style={{ color: LEVEL_COLOR[Math.max(1, (s.target || 3) - (s.current || 1)) + 1] || "#7C9A7E" }}>{Math.max(0, (s.target || 3) - (s.current || 1))} levels</p>
                    </div>
                  </div>
                  <textarea data-testid={`maturity-${p.id}-evidence`} placeholder="Evidence / observations (optional)" value={s.evidence || ""} onChange={e => setScore(p.id, { evidence: e.target.value })} className="w-full border border-sage-200 rounded-md px-2 py-1.5 text-xs min-h-[40px]" />
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      key: "diagnostic", label: "Diagnostic",
      render: ({ data }) => {
        const pillars = data.pillars || DEFAULT_PILLARS;
        const scores = data.scores || {};
        const rows = pillars.map(p => {
          const s = scores[p.id] || { current: 1, target: 3 };
          return { ...p, current: s.current || 1, target: s.target || 3, gap: Math.max(0, (s.target || 3) - (s.current || 1)) };
        });
        const overall = rows.length ? rows.reduce((a, r) => a + r.current, 0) / rows.length : 0;
        const overallTarget = rows.length ? rows.reduce((a, r) => a + r.target, 0) / rows.length : 0;
        const sortedGap = [...rows].sort((a, b) => b.gap - a.gap);
        // Radar
        const N = rows.length || 1; const C = 150;
        const angle = (i) => (Math.PI * 2 * i / N) - Math.PI / 2;
        const polyPoints = (key) => rows.map((r, i) => {
          const v = (r[key] / 5) * 100;
          return `${C + v * Math.cos(angle(i))},${C + v * Math.sin(angle(i))}`;
        }).join(" ");
        return (
          <div className="space-y-4" data-testid="maturity-diagnostic">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Overall (Now)</p><p className="font-heading text-2xl font-bold" style={{ color: LEVEL_COLOR[Math.round(overall)] }}>{overall.toFixed(1)} / 5</p></div>
              <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Overall (Target)</p><p className="font-heading text-2xl font-bold" style={{ color: LEVEL_COLOR[Math.round(overallTarget)] }}>{overallTarget.toFixed(1)} / 5</p></div>
              <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Pillars</p><p className="font-heading text-2xl font-bold text-sage-900">{rows.length}</p></div>
              <div className="bg-amber-50 rounded-md p-3 border border-amber-200"><p className="text-xs uppercase tracking-wider font-semibold text-amber-800">Largest gap</p><p className="font-heading text-base font-bold text-amber-900">{sortedGap[0]?.label || "—"} ({sortedGap[0]?.gap || 0} levels)</p></div>
            </div>
            <div className="bg-white border border-sage-200 rounded-md p-3 flex items-center justify-center" data-testid="maturity-radar">
              <svg viewBox="0 0 300 300" className="w-full max-w-md">
                {[20, 40, 60, 80, 100].map(r => (<circle key={r} cx="150" cy="150" r={r} fill="none" stroke="#E2E8E3" strokeWidth="0.5" />))}
                {rows.map((_, i) => (<line key={i} x1="150" y1="150" x2={150 + 100 * Math.cos(angle(i))} y2={150 + 100 * Math.sin(angle(i))} stroke="#E2E8E3" strokeWidth="0.5" />))}
                <polygon points={polyPoints("target")} fill="rgba(217, 119, 6, 0.12)" stroke="#D97706" strokeWidth="1.2" strokeDasharray="3,3" />
                <polygon points={polyPoints("current")} fill="rgba(124, 154, 126, 0.35)" stroke="#7C9A7E" strokeWidth="1.5" />
                {rows.map((r, i) => {
                  const lx = 150 + 115 * Math.cos(angle(i));
                  const ly = 150 + 115 * Math.sin(angle(i));
                  return (<text key={i} x={lx} y={ly} textAnchor="middle" fontSize="9" fill="#1E293B" fontWeight="600">{(r.label || "").slice(0, 14)}</text>);
                })}
              </svg>
            </div>
            <table className="w-full text-xs border border-sage-100 rounded-md overflow-hidden">
              <thead className="bg-sage-50 text-sage-600 uppercase tracking-wider"><tr><th className="text-left px-2 py-1.5">Pillar</th><th className="text-right px-2 py-1.5">Now</th><th className="text-right px-2 py-1.5">Target</th><th className="text-right px-2 py-1.5">Gap</th><th className="text-left px-2 py-1.5">Status</th></tr></thead>
              <tbody>
                {sortedGap.map((r, i) => (
                  <tr key={i} className="border-t border-sage-100">
                    <td className="px-2 py-1 font-semibold text-sage-900">{r.label}</td>
                    <td className="text-right px-2 py-1 font-mono"><span className="rounded px-1.5 py-0.5 text-white text-[10px]" style={{ backgroundColor: LEVEL_COLOR[r.current] }}>{r.current}</span></td>
                    <td className="text-right px-2 py-1 font-mono"><span className="rounded px-1.5 py-0.5 text-white text-[10px]" style={{ backgroundColor: LEVEL_COLOR[r.target] }}>{r.target}</span></td>
                    <td className="text-right px-2 py-1 font-mono font-bold">{r.gap}</td>
                    <td className="px-2 py-1">{r.gap === 0 ? <span className="text-green-700 text-xs font-semibold">On target</span> : r.gap >= 3 ? <span className="text-red-700 text-xs font-semibold">Major gap</span> : <span className="text-amber-700 text-xs font-semibold">Improve</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button data-testid="export-maturity-pdf" onClick={() => onExportPDF(data)} className="bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700 flex items-center gap-2"><FileText className="w-4 h-4" /> Export PDF</button>
          </div>
        );
      },
    },
  ];
  return <ToolWizard project={project} steps={steps} onSave={onSave} completedLabel="Finish Maturity Assessment" />;
}

export { DEFAULT_PILLARS, LEVEL_DEFS, LEVEL_COLOR };
